import { keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { Card } from '#pie/components/card/Card';
import { KpiCards } from '#pie/components/kpi-cards/KpiCards';
import { NavigationTile } from '#pie/components/navigation-tile/NavigationTile';
import { NewsCard } from '#pie/components/news-card/NewsCard';
import { Page } from '#pie/components/page/Page';
import { Skeleton } from '#pie/components/skeleton/Skeleton';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { HappyNewYear } from '#pie/components/winterfun/Fireworks';
import { SnowFlakes } from '#pie/components/winterfun/SnowFlakes';
import { VITE_FEATURE_FLAGS_PREFIX } from '#pie/env';
import { useProtectedMenu } from '#pie/hooks/useProtectedMenu';
import { screenSizes, useScreenSize } from '#pie/hooks/useScreenSize';
import { useUserInfo } from '#pie/hooks/useUserInfo';
import {
  AuthorizationRoles,
  useGetFeatureFlags,
} from '#pie/internal/api/pie-bff';
import { trpc } from '#pie/trpc';

export function Home() {
  const { t } = useTranslation();
  const [{ data: userInfo, isLoading }] = useUserInfo();
  const name = userInfo && `${userInfo.given_name} ${userInfo.family_name}`;
  const hasTicketRole = useUserHasRole({ role: AuthorizationRoles.Tickets });
  const { mainMenu } = useProtectedMenu();

  const { data: news, isLoading: isNewsLoading } =
    trpc.pages.news.recentItems.useQuery(
      { limit: 5 },
      { placeholderData: keepPreviousData },
    );

  const currentSize = useScreenSize();
  // TODO: Write tests for this
  /* istanbul ignore next -- @preserve */
  const shownNews = news?.slice(
    0,
    currentSize && screenSizes.indexOf(currentSize) > screenSizes.indexOf('2xl')
      ? 5
      : 4,
  );

  const { data: flags, isPending } = useGetFeatureFlags({
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    },
  });

  const showSnowfall = flags?.[`${VITE_FEATURE_FLAGS_PREFIX}snowfall`] === true;
  const showFireworks =
    flags?.[`${VITE_FEATURE_FLAGS_PREFIX}fireworks`] === true;
  const showKpiCards = flags?.[`${VITE_FEATURE_FLAGS_PREFIX}kpicards`] === true;

  return (
    <Page>
      {showFireworks && <HappyNewYear />}
      {showSnowfall && <SnowFlakes />}
      <Stack gap="lg" className="py-6">
        <Stack as="section" gap="lg">
          <Text variant="h1" as="h1">
            {!isLoading ? (
              t('home.header.title', { name })
            ) : (
              <Skeleton className="max-w-xl" />
            )}
          </Text>
          {isPending ? (
            <Skeleton className="h-48" />
          ) : showKpiCards && hasTicketRole ? (
            <KpiCards />
          ) : (
            <Card className="p-6">
              <Stack gap="lg">
                <Text variant="h4" as="h2">
                  {t('home.pages.title')}
                </Text>
                <div className="grid grid-cols-[repeat(auto-fill,_minmax(18rem,_1fr))] gap-6">
                  {[...mainMenu].map((menuItem) => (
                    <NavigationTile
                      key={`navtile-${menuItem.to}`}
                      {...menuItem}
                    />
                  ))}
                </div>
              </Stack>
            </Card>
          )}
        </Stack>
        {(isNewsLoading || (shownNews && shownNews.length > 0)) && (
          <Stack as="section" gap="lg">
            <Text variant="h4" as="h2">
              {t('common.news.title')}
            </Text>
            <div className="grid grid-cols-[repeat(auto-fill,_minmax(22rem,_1fr))] gap-6">
              {isNewsLoading
                ? Array(3)
                    .fill(null)
                    .map((_, i) => (
                      <NewsCard as="div" isLoading key={`news-${i}`} />
                    ))
                : shownNews?.map((newsItem) => (
                    <div key={newsItem.id}>
                      <NewsCard
                        to={newsItem.uri}
                        title={newsItem.title}
                        description={newsItem.excerpt}
                        imageUrl={newsItem.image.url}
                        date={newsItem.postDate}
                      />
                    </div>
                  ))}
            </div>
          </Stack>
        )}
      </Stack>
    </Page>
  );
}
